import { useState } from "react";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { generateId } from "utils/accessibility";
import { formField, signer } from "theme/colors";
import deleteAccountTokens from "./DeleteAccountFlow.tokens";

const t = deleteAccountTokens;

const h1Sx = {
  m: 0,
  fontSize: { xs: "20px", sm: "23px", md: "28px" },
  fontWeight: 800,
  letterSpacing: "-.01em",
  color: t.color.ink,
  lineHeight: 1.25,
};

const sectionLabelSx = {
  fontSize: "11px",
  fontWeight: 800,
  letterSpacing: ".09em",
  color: t.color.label,
};

const cardSx = {
  bgcolor: "#fff",
  borderRadius: t.radius.card,
  boxShadow: t.color.cardShadow,
  p: { xs: "22px 20px", md: "26px 28px" },
};

const listItemSx = {
  display: "flex",
  alignItems: "flex-start",
  gap: { xs: "11px", md: "12px" },
  py: { xs: "11px", md: "13px" },
  borderBottom: `1px solid ${t.color.hairline}`,
  fontSize: { xs: "13px", md: "13.5px" },
  lineHeight: 1.5,
};

const fieldLabelSx = {
  fontSize: "12.5px",
  fontWeight: 800,
  color: t.color.ink,
};

const inputBoxSx = (borderColor: string) => ({
  height: "46px",
  border: `1.5px solid ${borderColor}`,
  borderRadius: t.radius.input,
  bgcolor: t.color.inputBg,
  boxSizing: "border-box" as const,
});

interface DeleteAccountFlowProps {
  onBack: () => void;
  onConfirm?: (password: string) => void;
  loading?: boolean;
}

const RemovesList = () => (
  <Stack sx={{ mt: { xs: "12px", md: "14px" } }}>
    {t.removes.map((text) => (
      <Box key={text} sx={listItemSx}>
        <CloseRoundedIcon
          sx={{ fontSize: 15, color: t.color.danger, flexShrink: 0, mt: "1px" }}
        />
        <Box component="span" sx={{ color: t.color.listText }}>
          {text}
        </Box>
      </Box>
    ))}
  </Stack>
);

const ChecksList = () => (
  <Stack spacing={{ xs: "11px", md: "12px" }} sx={{ mt: { xs: "13px", md: "14px" } }}>
    {t.checks.map((text) => (
      <Box
        key={text}
        sx={{ display: "flex", alignItems: "flex-start", gap: { xs: "10px", md: "11px" } }}
      >
        <CheckRoundedIcon
          sx={{ fontSize: 15, color: t.color.success, flexShrink: 0, mt: "1px" }}
        />
        <Box
          component="span"
          sx={{ fontSize: { xs: "12.5px", md: "13px" }, lineHeight: 1.5, color: t.color.body }}
        >
          {text}
        </Box>
      </Box>
    ))}
  </Stack>
);

const DeleteAccountFlow = ({
  onBack,
  onConfirm,
  loading = false,
}: DeleteAccountFlowProps) => {
  const [confirmText, setConfirmText] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [confirmFieldId] = useState(() => generateId("delete-confirm"));
  const [passwordFieldId] = useState(() => generateId("delete-password"));

  const armed = confirmText.trim().toUpperCase() === "DELETE";
  const canSubmit = armed && password.trim().length > 0 && !loading;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm?.(password);
  };

  return (
    <Box>
      <Box
        component="button"
        type="button"
        onClick={onBack}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "5px",
          border: "none",
          background: "none",
          p: 0,
          mb: { xs: "16px", md: "20px" },
          fontFamily: "inherit",
          fontSize: { xs: "12.5px", md: "13px" },
          fontWeight: 700,
          color: t.color.body,
          cursor: "pointer",
        }}
      >
        <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
        Back to settings
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: { xs: "13px", md: "14px" } }}>
        <Box
          sx={{
            width: { xs: 46, md: 54 },
            height: { xs: 46, md: 54 },
            borderRadius: t.radius.tile,
            bgcolor: t.color.dangerTile,
            color: t.color.danger,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <DeleteOutlineRoundedIcon sx={{ fontSize: { xs: 22, md: 26 } }} />
        </Box>
        <Box>
          <Typography component="h1" sx={h1Sx}>
            Delete your Inkfree account
          </Typography>
          <Typography
            sx={{
              m: 0,
              mt: "7px",
              maxWidth: 560,
              fontSize: { xs: "13px", md: "14px" },
              lineHeight: 1.6,
              color: t.color.body,
            }}
          >
            This permanently erases your organization's data. There is no
            recovery and no grace period.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          mt: { xs: "22px", md: "32px" },
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 400px" },
          gap: { xs: "16px", md: "24px" },
          alignItems: "start",
        }}
      >
        {/* LEFT: consequences */}
        <Stack spacing={{ xs: "16px", md: "20px" }}>
          <Box sx={cardSx}>
            <Typography component="h2" sx={{ m: 0, ...sectionLabelSx }}>
              WHAT GETS DELETED
            </Typography>
            <RemovesList />
          </Box>

          <Box sx={cardSx}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: t.color.amber }} />
              <Typography component="h2" sx={{ m: 0, fontSize: "13px", fontWeight: 800, color: t.color.ink }}>
                Do these first
              </Typography>
            </Box>
            <ChecksList />
          </Box>
        </Stack>

        {/* RIGHT: confirm panel */}
        <Box
          sx={{
            position: { md: "sticky" },
            top: { md: "24px" },
            bgcolor: "#fff",
            borderRadius: t.radius.card,
            boxShadow: t.color.confirmShadow,
            borderTop: `3px solid ${t.color.danger}`,
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: { xs: "22px 20px 0", md: "24px 26px 0" } }}>
            <Typography
              component="h2"
              sx={{ m: 0, fontSize: { xs: "15px", md: "16px" }, fontWeight: 800, color: t.color.ink }}
            >
              Confirm deletion
            </Typography>
            <Typography
              sx={{ m: 0, mt: "7px", fontSize: "12.5px", lineHeight: 1.55, color: t.color.body }}
            >
              Two steps to be sure. This can't be reversed.
            </Typography>
          </Box>

          <Box sx={{ p: { xs: "18px 20px 22px", md: "20px 26px 26px" } }}>
            <Stack spacing="9px">
              <Typography component="label" htmlFor={confirmFieldId} sx={fieldLabelSx}>
                Type{" "}
                <Box
                  component="span"
                  sx={{
                    fontFamily: "ui-monospace, Menlo, monospace",
                    color: t.color.danger,
                    bgcolor: t.color.dangerTile,
                    px: "7px",
                    py: "1px",
                    borderRadius: "5px",
                  }}
                >
                  DELETE
                </Box>{" "}
                to confirm
              </Typography>
              <Box
                component="input"
                id={confirmFieldId}
                value={confirmText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setConfirmText(e.target.value)
                }
                placeholder="DELETE"
                autoComplete="off"
                sx={{
                  ...inputBoxSx(armed ? t.color.danger : t.color.inputBorder),
                  px: "14px",
                  font: '700 14px ui-monospace, Menlo, monospace',
                  letterSpacing: ".12em",
                  color: t.color.ink,
                  outline: "none",
                  width: "100%",
                  "&::placeholder": { color: formField.placeholderMuted },
                }}
              />
            </Stack>

            <Stack spacing="9px" sx={{ mt: { xs: "14px", md: "16px" } }}>
              <Typography component="label" htmlFor={passwordFieldId} sx={fieldLabelSx}>
                Account password
              </Typography>
              <Box
                sx={{
                  ...inputBoxSx(t.color.inputBorder),
                  display: "flex",
                  alignItems: "stretch",
                  overflow: "hidden",
                }}
              >
                <Box
                  component="input"
                  id={passwordFieldId}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setPassword(e.target.value)
                  }
                  autoComplete="current-password"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    bgcolor: "transparent",
                    px: "14px",
                    fontFamily: "inherit",
                    fontSize: "14px",
                    fontWeight: 600,
                    letterSpacing: ".04em",
                    color: t.color.ink,
                    outline: "none",
                  }}
                />
                <Box
                  component="button"
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  sx={{
                    width: "50px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: signer.text.subtitle,
                    cursor: "pointer",
                    border: "none",
                    borderLeft: "none",
                    background: "none",
                    flexShrink: 0,
                  }}
                >
                  {showPassword ? (
                    <VisibilityOutlinedIcon sx={{ fontSize: 19 }} />
                  ) : (
                    <VisibilityOffOutlinedIcon sx={{ fontSize: 19 }} />
                  )}
                </Box>
              </Box>
            </Stack>

            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleSubmit}
              disabled={!canSubmit}
              startIcon={
                loading ? (
                  <CircularProgress size={16} thickness={5} sx={{ color: "inherit" }} />
                ) : (
                  <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{
                mt: "22px",
                height: "48px",
                borderRadius: t.radius.input,
                fontSize: "14px",
                fontWeight: 700,
                textTransform: "none",
                color: "#fff",
                bgcolor: armed ? t.color.dangerArmedBtnBg : t.color.dangerIdleBtnBg,
                "&:hover": {
                  bgcolor: armed ? t.color.dangerArmedBtnBg : t.color.dangerIdleBtnBg,
                },
                "&.Mui-disabled": {
                  color: "#fff",
                  bgcolor: t.color.dangerIdleBtnBg,
                  opacity: 0.85,
                },
              }}
            >
              {loading ? "Deleting…" : "Permanently delete"}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={onBack}
              disabled={loading}
              sx={{
                mt: "10px",
                height: "44px",
                borderRadius: t.radius.input,
                borderColor: t.color.inputBorder,
                fontSize: "13.5px",
                fontWeight: 700,
                textTransform: "none",
                color: t.color.ink,
              }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DeleteAccountFlow;
